import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Calendar, Clock, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScheduleDetails() {
  const navigate = useNavigate();
  const { module } = useParams();
  const [student, setStudent] = useState(null);
  const [schedule, setSchedule] = useState([]);

  useEffect(() => {
    const studentData = localStorage.getItem("student");
    if (!studentData) {
      navigate("/");
      return;
    }
    
    const parsedStudent = JSON.parse(studentData);
    setStudent(parsedStudent);
    
    // Get schedule for this module
    if (parsedStudent.schedules && parsedStudent.schedules[module]) {
      setSchedule(parsedStudent.schedules[module]);
    }
  }, [module]);

  const getAttendanceStatus = (day) => {
    if (!student?.batch_start_date) return null;
    
    const today = new Date();
    const dayOfWeek = today.toLocaleDateString('en-US', { weekday: 'long' });
    
    // Only show attendance for today's class
    if (day !== dayOfWeek) return null;
    
    const todayStr = today.toISOString().split('T')[0];
    const attended = student.attendance_records?.some(record => 
      record.date.startsWith(todayStr)
    );
    
    return attended ? "Present" : "Absent";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate("/student")}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-4xl font-bold text-gray-900 capitalize">
            {module.replace('_', ' ')} Class Schedule
          </h1>
          <p className="text-gray-600 mt-2">Complete schedule with attendance tracking</p>
        </div>

        {/* Schedule Grid */}
        <div className="grid gap-4">
          {schedule.map((cls, idx) => {
            const status = getAttendanceStatus(cls.day);
            
            return (
              <Card key={idx} className="border-2 hover:shadow-lg transition" data-testid={`schedule-item-${idx}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-6 flex-1">
                      <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg">
                        <Calendar className="w-8 h-8 text-white" />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{cls.day}</h3>
                        <div className="flex items-center gap-2 text-gray-600 mb-2">
                          <Clock className="w-4 h-4" />
                          <span className="font-semibold">{cls.time}</span>
                        </div>
                        <p className="text-gray-700">{cls.topic}</p>
                      </div>
                    </div>

                    {/* Attendance Badge */}
                    {status && (
                      <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                        status === "Present" 
                          ? "bg-green-100 text-green-700 border-2 border-green-300" 
                          : "bg-red-100 text-red-700 border-2 border-red-300"
                      }`}>
                        {status === "Present" ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                        <span className="font-semibold">{status}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {schedule.length === 0 && (
          <Card className="border-2">
            <CardContent className="p-12 text-center">
              <p className="text-gray-500 text-lg">No schedule available for this module</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
